/**
 * Master-Findings-Editor: kanonischer Findings-Bestand (DB-only, kein Obsidian).
 *
 * Linke Spalte: Liste aller Findings, gefiltert nach Status / Stichwort.
 * Rechte Spalte: Detail des ausgewaehlten Findings — Original, Fix-Versionen,
 *   Joerg-Kommentare, Test-Pfade — alle mit Add/Delete-Knoepfen.
 *
 * Joerg legt neue Funde an, ergaenzt Kommentare nach jeder Tester-Welle, ich
 * (Claude) schreibe `Fix vN+1` direkt ueber API. Keine Vault-Roundtrips mehr.
 */
import { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Trash2, Search, FileText, GitCommit, MessageSquare, Link as LinkIcon, RefreshCw } from 'lucide-react';
import { testCockpitApi, type MasterFinding } from '../../../lib/testCockpitApi';

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  'offen':           { label: 'offen',           cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300' },
  'in Verfeinerung': { label: 'in Verfeinerung', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  'final':           { label: 'final',           cls: 'bg-brand-500/15 text-brand-700 dark:text-brand-300' },
  'obsolete':        { label: 'obsolete',        cls: 'bg-slate-500/15 text-slate-500' },
};

export default function MasterFindingsEditor() {
  const [findings, setFindings] = useState<MasterFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    testCockpitApi.listMasterFindings()
      .then(fs => {
        setFindings(fs);
        if (selectedId && !fs.some(f => f.id === selectedId)) setSelectedId(null);
      })
      .catch(() => setFindings([]))
      .finally(() => setLoading(false));
  }, [selectedId]);

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = findings.filter(f => {
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!f.title.toLowerCase().includes(s) && !f.source_label.toLowerCase().includes(s) && !(f.original_text ?? '').toLowerCase().includes(s)) return false;
    }
    return true;
  });
  const selected = findings.find(f => f.id === selectedId) ?? null;

  async function importVault() {
    if (!confirm('Findings aus vault_files importieren? Bestehende Funde werden NICHT ueberschrieben (overwrite=false). Bei „OK mit Ueberschreiben" werden Fix-Versionen, Kommentare und Test-Pfade frisch reimportiert.')) return;
    const overwrite = confirm('Mit Ueberschreiben (alte Sub-Tabellen-Eintraege loeschen + neu schreiben)? Abbrechen = nur Neu-Eintraege ergaenzen.');
    setBusy(true);
    try {
      const r = await testCockpitApi.importVault(overwrite);
      alert(`Importiert: ${r.findings} Findings · ${r.fix_versions} Fix-Versionen · ${r.admin_comments} Kommentare · ${r.test_paths} Test-Pfade`);
      reload();
    } catch (e) { alert(`Import fehlgeschlagen: ${(e as Error).message}`); }
    finally { setBusy(false); }
  }

  async function newFinding() {
    const sourceLabel = prompt('Source-Label (Welle / Datei) — z.B. 2026-05-05-runde-3:');
    if (!sourceLabel) return;
    const title = prompt('Titel des Funds:');
    if (!title) return;
    const original = prompt('Original-Wortlaut (Joergs Beschreibung):') ?? '';
    setBusy(true);
    try {
      // Naechster idx innerhalb der source_label
      const sameSource = findings.filter(f => f.source_label === sourceLabel);
      const nextIdx = sameSource.reduce((max, f) => Math.max(max, f.idx + 1), 0);
      const created = await testCockpitApi.createMasterFinding({
        source_label: sourceLabel,
        idx: nextIdx,
        title,
        original_text: original,
        original_date: new Date().toISOString().slice(0, 10),
        status: 'offen',
      });
      reload();
      setSelectedId(created.id);
    } catch (e) { alert(`Anlegen fehlgeschlagen: ${(e as Error).message}`); }
    finally { setBusy(false); }
  }

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground py-6"><Loader2 size={14} className="animate-spin" /> Findings laden...</div>;
  }

  return (
    <div className="bg-card border border-default rounded-lg flex flex-col" style={{ minHeight: '700px' }}>
      <div className="px-4 py-3 border-b border-default flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-brand-500" />
          <span className="font-medium">Findings (Master)</span>
          <span className="text-xs text-muted-foreground">{filtered.length} / {findings.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={newFinding} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-primary text-white hover:bg-primary/90 disabled:opacity-50">
            <Plus size={14} /> Neu
          </button>
          <button onClick={importVault} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-card border border-default hover:bg-primary/5 disabled:opacity-50" title="Aus vault_files re-importieren">
            <RefreshCw size={14} /> Vault-Import
          </button>
        </div>
      </div>

      <div className="px-4 py-2 border-b border-default flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={12} className="absolute left-2 top-2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Suche Titel / Original / Source..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-7 pr-2 py-1.5 rounded bg-background border border-default text-sm w-full"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-1.5 rounded bg-background border border-default text-sm">
          <option value="all">alle Status</option>
          <option value="offen">offen</option>
          <option value="in Verfeinerung">in Verfeinerung</option>
          <option value="final">final</option>
          <option value="obsolete">obsolete</option>
        </select>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Liste */}
        <div className="w-1/3 border-r border-default overflow-y-auto" style={{ maxHeight: '650px' }}>
          {filtered.length === 0 && <div className="p-4 text-sm text-muted-foreground italic">Keine Findings.</div>}
          {filtered.map(f => {
            const badge = STATUS_LABEL[f.status] ?? STATUS_LABEL['offen'];
            const isSel = f.id === selectedId;
            return (
              <button
                key={f.id}
                onClick={() => setSelectedId(f.id)}
                className={`block w-full text-left px-3 py-2 border-b border-default/50 hover:bg-primary/5 ${isSel ? 'bg-brand-500/10' : ''}`}
              >
                <div className="flex items-start gap-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${badge.cls}`}>{badge.label}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{f.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{f.source_label} · #{f.idx + 1} · {f.fix_versions.length} Fix(es)</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail */}
        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: '650px' }}>
          {!selected ? (
            <div className="text-sm text-muted-foreground italic">Finding aus der Liste waehlen — oder mit „Neu" anlegen.</div>
          ) : (
            <FindingDetail finding={selected} onChanged={reload} />
          )}
        </div>
      </div>
    </div>
  );
}

function FindingDetail({ finding, onChanged }: { finding: MasterFinding; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);

  async function setStatus(status: MasterFinding['status']) {
    setBusy(true);
    try { await testCockpitApi.updateMasterFinding(finding.id, { status }); onChanged(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  }

  async function editOriginal() {
    const t = prompt('Original-Wortlaut Jörg:', finding.original_text ?? '');
    if (t === null) return;
    setBusy(true);
    try { await testCockpitApi.updateMasterFinding(finding.id, { original_text: t }); onChanged(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  }
  async function editTitle() {
    const t = prompt('Titel:', finding.title);
    if (!t) return;
    setBusy(true);
    try { await testCockpitApi.updateMasterFinding(finding.id, { title: t }); onChanged(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  }

  async function addFix() {
    const text = prompt('Fix-Beschreibung:');
    if (!text) return;
    const commit = prompt('Commit-Hash (optional):') ?? undefined;
    setBusy(true);
    try {
      await testCockpitApi.addFixVersion(finding.id, {
        fix_text: text,
        commit_hash: commit || undefined,
        fix_date: new Date().toISOString().slice(0, 10),
      });
      onChanged();
    } catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  }

  async function addComment() {
    const text = prompt('Joerg-Kommentar:');
    if (!text) return;
    setBusy(true);
    try {
      await testCockpitApi.addAdminComment(finding.id, {
        comment_text: text,
        comment_date: new Date().toISOString().slice(0, 10),
        sort_order: finding.admin_comments.length,
      });
      onChanged();
    } catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  }

  async function addTestPath() {
    const persona = prompt('Persona (handwerker / gf / admin):', 'handwerker');
    const url = prompt('URL-Pfad (z.B. /m/bauberichteV2/tasks/<id>?scrollTo=timer):');
    if (!url) return;
    const hint = prompt('Hinweis fuer Tester:') ?? undefined;
    const expectation = prompt('Erwartetes Verhalten:') ?? undefined;
    setBusy(true);
    try {
      await testCockpitApi.addTestPath(finding.id, {
        persona_key: persona || undefined,
        url_path: url,
        hint: hint || undefined,
        expectation: expectation || undefined,
        sort_order: finding.test_paths.length,
      });
      onChanged();
    } catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  }

  async function deleteFix(version: number) {
    if (!confirm(`Fix v${version} loeschen?`)) return;
    // Backend identifiziert via finding-fix-versions/:id — wir brauchen die id, aber unsere API liefert nur version.
    // Workaround: GET der Findings hat keine ids fuer fix_versions. Fallback: kompletter Re-Sync via overwrite.
    alert('Loeschen einzelner Fix-Versionen via UI noch nicht — bitte ueber DB.');
  }
  async function deleteComment(id: string) {
    if (!confirm('Kommentar loeschen?')) return;
    setBusy(true);
    try { await testCockpitApi.deleteAdminComment(id); onChanged(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  }
  async function deleteTestPath(id: string) {
    if (!confirm('Test-Pfad loeschen?')) return;
    setBusy(true);
    try { await testCockpitApi.deleteTestPath(id); onChanged(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  }

  const badge = STATUS_LABEL[finding.status] ?? STATUS_LABEL['offen'];
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground">{finding.source_label} · #{finding.idx + 1}</div>
          <button onClick={editTitle} disabled={busy} className="text-lg font-semibold text-left hover:underline">{finding.title}</button>
        </div>
        <div className="flex items-center gap-1">
          {(['offen', 'in Verfeinerung', 'final', 'obsolete'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              disabled={busy || finding.status === s}
              className={`text-xs px-2 py-1 rounded ${finding.status === s ? badge.cls + ' ring-2 ring-offset-1 ring-current' : 'border border-default text-muted-foreground hover:text-foreground'}`}
            >
              {STATUS_LABEL[s].label}
            </button>
          ))}
        </div>
      </div>

      {/* Original */}
      <div className="border border-default rounded p-3 bg-background/40">
        <div className="text-xs text-muted-foreground mb-1 flex items-center justify-between">
          <span>Original Jörg{finding.original_date ? ` [${finding.original_date.slice(0, 10)}]` : ''}</span>
          <button onClick={editOriginal} disabled={busy} className="text-xs hover:text-foreground">bearbeiten</button>
        </div>
        <div className="text-sm whitespace-pre-wrap">{finding.original_text || <span className="italic text-muted-foreground">(leer)</span>}</div>
      </div>

      {/* Fix-Versionen */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><GitCommit size={12} /> Fix-Versionen</div>
          <button onClick={addFix} disabled={busy} className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20">
            <Plus size={10} /> Fix vN+1
          </button>
        </div>
        <div className="space-y-2">
          {finding.fix_versions.length === 0 && <div className="text-sm text-muted-foreground italic">Noch kein Fix.</div>}
          {finding.fix_versions.map(fv => (
            <div key={fv.version} className="border border-default rounded p-2 text-sm">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>v{fv.version} {fv.fix_date ? `· ${fv.fix_date.slice(0, 10)}` : ''} {fv.commit_hash ? `· ${fv.commit_hash}` : ''}</span>
                <button onClick={() => deleteFix(fv.version)} disabled={busy} className="hover:text-rose-500"><Trash2 size={11} /></button>
              </div>
              <div className="whitespace-pre-wrap">{fv.fix_text}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Joerg-Kommentare */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><MessageSquare size={12} /> Joerg-Kommentare</div>
          <button onClick={addComment} disabled={busy} className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20">
            <Plus size={10} /> Kommentar
          </button>
        </div>
        <div className="space-y-2">
          {finding.admin_comments.length === 0 && <div className="text-sm text-muted-foreground italic">Keine Kommentare.</div>}
          {[...finding.admin_comments].sort((a, b) => a.sort_order - b.sort_order).map(c => (
            <div key={c.id} className="border border-default rounded p-2 text-sm">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>{c.comment_date ? c.comment_date.slice(0, 10) : '—'}</span>
                <button onClick={() => deleteComment(c.id)} disabled={busy} className="hover:text-rose-500"><Trash2 size={11} /></button>
              </div>
              <div className="whitespace-pre-wrap">{c.comment_text}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Test-Pfade */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><LinkIcon size={12} /> Test-Pfade</div>
          <button onClick={addTestPath} disabled={busy} className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20">
            <Plus size={10} /> Pfad
          </button>
        </div>
        <div className="space-y-2">
          {finding.test_paths.length === 0 && <div className="text-sm text-muted-foreground italic">Kein Test-Pfad.</div>}
          {[...finding.test_paths].sort((a, b) => a.sort_order - b.sort_order).map(tp => (
            <div key={tp.id} className="border border-default rounded p-2 text-sm">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>{tp.persona_key ? `tester-${tp.persona_key}` : '(beliebige Persona)'} · {tp.stage}</span>
                <button onClick={() => deleteTestPath(tp.id)} disabled={busy} className="hover:text-rose-500"><Trash2 size={11} /></button>
              </div>
              <div className="font-mono text-xs">{tp.url_path}</div>
              {tp.hint && <div className="text-xs text-muted-foreground mt-1">{tp.hint}</div>}
              {tp.expectation && <div className="text-xs mt-1"><strong>Erwartung:</strong> {tp.expectation}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
